import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Injector,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { OKTA_AUTH, OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { BaseApiUrlInterceptor } from './core/interceptors/base-api.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { ErrorHandlerInterceptor } from './core/interceptors/error-handler.interceptor';
import { EntraAuthInterceptor } from './core/interceptors/entra-auth.interceptor';
import OktaAuth from '@okta/okta-auth-js';
import { AUTH_PROVIDER_TOKEN, AuthProvider } from './core/tokens/auth-provider.token';
import { AuthenticationService } from './services/authentication.service';
import { TokenStorageService } from './services/token-storage.service';
import { UserManagementService } from './services/user-management.service';
import { SnackbarService } from './services/snackbar.service';
import { AppConfigProvider } from './core/providers/app-config.provider';
import { oktaAuth, oktaStubAuth } from './core/consts/api.const';
import { OktaAuthInterceptor } from './core/interceptors/okta-auth.interceptor';
import { API_SERVER_URL_TOKEN } from './core/tokens/api-server-url.token';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import {
  msalGuardConfigFactory,
  msalInstanceFactory,
  msalInterceptorConfigFactory,
} from './core/consts/msal.const';

function onAuthRequired(_oktaAuth: OktaAuth, injector: Injector) {
  const router = injector.get(Router);
  router.navigate(['/login']);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    importProvidersFrom(OktaAuthModule.forRoot({ oktaAuth })),
    importProvidersFrom(MatNativeDateModule),

    // ─── Okta ──────────────────────────────────────────────────────────────
    {
      provide: OKTA_AUTH,
      deps: [OKTA_CONFIG],
      useFactory: (oktaConfig: any) => oktaConfig.oktaAuth,
    },

    {
      provide: API_SERVER_URL_TOKEN,
      deps: [AppConfigProvider],
      useFactory: (appConfigProvider: AppConfigProvider) =>
        appConfigProvider.appConfig.apiServerUrl,
    },

    {
      provide: OKTA_CONFIG,
      deps: [AUTH_PROVIDER_TOKEN],
      useFactory: (authProvider: AuthProvider) => ({
        oktaAuth: authProvider === 'Okta' ? oktaAuth : oktaStubAuth,
        onAuthRequired,
      }),
    },

    // ─── Microsoft Entra (MSAL) ────────────────────────────────────────────
    // MSAL providers are always registered; the instance is a stub when Entra
    // is not the active provider so that DI remains consistent.
    {
      provide: MSAL_INSTANCE,
      deps: [AUTH_PROVIDER_TOKEN],
      useFactory: (authProvider: AuthProvider) => msalInstanceFactory(authProvider === 'Entra'),
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // ─── HTTP interceptors ─────────────────────────────────────────────────
    { provide: HTTP_INTERCEPTORS, useClass: BaseApiUrlInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      deps: [
        AUTH_PROVIDER_TOKEN,
        HttpClient,
        TokenStorageService,
        UserManagementService,
        AuthenticationService,
        SnackbarService,
        Router,
        OKTA_AUTH,
        MsalService,
      ],
      useFactory: (
        authProvider: AuthProvider,
        httpClient: HttpClient,
        tokenStorageService: TokenStorageService,
        userManagementService: UserManagementService,
        authenticationService: AuthenticationService,
        snackbarService: SnackbarService,
        router: Router,
        oktaAuth: OktaAuth,
        msalService: MsalService,
      ) => {
        if (authProvider === 'Entra') {
          return new EntraAuthInterceptor(msalService);
        }
        return authProvider === 'Okta'
          ? new OktaAuthInterceptor(httpClient, oktaAuth)
          : new AuthInterceptor(
              tokenStorageService,
              userManagementService,
              authenticationService,
              snackbarService,
              router,
            );
      },
      multi: true,
    },

    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerInterceptor, multi: true },

    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },

    AuthenticationService,
    TokenStorageService,
    UserManagementService,
    SnackbarService,

    // ─── App initializers ─────────────────────────────────────────────────
    {
      provide: APP_INITIALIZER,
      useFactory: (
        authProvider: AuthProvider,
        router: Router,
        appConfigProvider: AppConfigProvider,
        oktaAuth: OktaAuth,
      ) => {
        return () => {
          if (authProvider === 'Okta') {
            oktaAuth.tokenManager.on('renewed', (key, newToken, oldToken) => {});

            oktaAuth.tokenManager.on('error', (error) => {
              router.navigate(['/login']);
            });

            oktaAuth.tokenManager.on('expired', (key, expiredToken) => {
              oktaAuth.tokenManager.renew(key).catch((err) => {
                router.navigate(['/login']);
              });
            });

            return oktaAuth
              .start()
              .then(() => {})
              .catch((error) => {});
          }

          return Promise.resolve();
        };
      },
      multi: true,
      deps: [AUTH_PROVIDER_TOKEN, Router, AppConfigProvider, OKTA_AUTH],
    },

    // Initialize MSAL and handle any pending redirect response.
    // handleRedirectObservable() MUST be called here (before routing) to clear
    // the msal.interaction.status from sessionStorage, otherwise any subsequent
    // call to loginRedirect() throws interaction_in_progress.
    {
      provide: APP_INITIALIZER,
      useFactory: (authProvider: AuthProvider, msalService: MsalService) => {
        return async () => {
          if (authProvider === 'Entra') {
            await msalService.instance.initialize();
            await firstValueFrom(msalService.handleRedirectObservable());
          }
        };
      },
      multi: true,
      deps: [AUTH_PROVIDER_TOKEN, MsalService],
    },
  ],
};
